import { useAlert } from "contexts/alertContext";
import { getWorkshopEvaluation } from "framework/workshopEvaluation";
import { handleError } from "helpers/utils";
import { Feature } from "models/api";
import React, { useCallback, useEffect, useState } from "react";
import { QueryClient } from "react-query";
import { useParams } from "react-router-dom";

interface EvaluationResult {
    fullName: string;
    questionTitle: string;
    optionDescription: string;
    dimensionDescription: string;
  }
  
  interface GroupedData {
    [dimension: string]: {
      questions: string[];
      answers: { [employee: string]: { [question: string]: string } };
    };
  }
  
  interface Props {
    feature: {
      singleName: string;
    };
  }
  
  const EvaluationReport: React.FC<Props> = ({ feature }) => {
    const queryClient = new QueryClient();
    const urlParams = useParams();
    const { setAlert } = useAlert();
  
    const id: string = urlParams[`${feature.singleName}Id`] as string;
    const [data, setData] = useState<EvaluationResult[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
  
    const fetchEvaluationResult = useCallback(async () => {
      setLoading(true);
      try {
        const response: EvaluationResult[] = await queryClient.fetchQuery(
          "get workshop evaluation",
          () => getWorkshopEvaluation(`/getevaluationresult?workshopId=${id}`)
        );
        setData(response || []);
      } catch (error: any) {
        setAlert({
          open: true,
          message: handleError(error) || "Something went wrong",
          type: "error",
        });
      } finally {
        setLoading(false);
      }
    }, [id, queryClient, setAlert]);
  
    useEffect(() => {
      fetchEvaluationResult();
    }, []);
  
    // Group data by dimensions
    const groupedData: GroupedData = data.reduce((acc, curr) => {
      const { dimensionDescription, questionTitle, fullName, optionDescription } = curr;
  
      if (!acc[dimensionDescription]) {
        acc[dimensionDescription] = { questions: [], answers: {} };
      }
  
      if (!acc[dimensionDescription].questions.includes(questionTitle)) {
        acc[dimensionDescription].questions.push(questionTitle);
      }
  
      if (!acc[dimensionDescription].answers[fullName]) {
        acc[dimensionDescription].answers[fullName] = {};
      }
  
      acc[dimensionDescription].answers[fullName][questionTitle] = optionDescription;
  
      return acc;
    }, {} as GroupedData);
  
    const dimensions = Object.keys(groupedData);
    const employees = Array.from(new Set(data.map((d) => d.fullName)));
  
    if (loading) {
      return <div>Loading...</div>;
    }
  
    if (!data.length) {
      return <div>No evaluation data available</div>;
    }
  
    return (
      <div style={{ overflowX: "auto" }}>
        <table border={1} cellPadding={8} style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead>
            <tr>
              <th rowSpan={2}>Employee</th>
              {dimensions.map((dimension) => (
                <th key={dimension} colSpan={groupedData[dimension].questions.length}>
                  {dimension}
                </th>
              ))}
            </tr>
            <tr>
              {dimensions.map((dimension) =>
                groupedData[dimension].questions.map((question) => (
                  <th key={dimension + question}>{question}</th>
                ))
              )}
            </tr>
          </thead>
          <tbody>
            {employees.map((employee) => (
              <tr key={employee}>
                <td>{employee}</td>
                {dimensions.map((dimension) =>
                  groupedData[dimension].questions.map((question) => (
                    <td key={employee + dimension + question}>
                      {groupedData[dimension].answers[employee]?.[question] || "N/A"}
                    </td>
                  ))
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };
  
  export default EvaluationReport;